import { useCallback, useEffect, useRef } from 'react';
import { v2Validate, V2ValidateResponse, DivisionMembership } from 'src/store/modules/auth/v2Operations';
import { useLocation } from 'src/utils/Hooks';
import { getAuthVersion } from 'src/utils/authVersion';
import { saveDivisions, saveCurrentDivisionId } from 'src/utils/divisionStorage';
import { buildSsoSignInUrl, getEnterpriseSsoHost, getReferrerInfo, isSsoHost } from 'src/utils/ssoReferrer';

export const DIVISION_VALIDATED_EVENT = 'sl:division-validated';

const SSO_PATHS = ['/switch-division', '/sign_out', '/logout', '/auth/callback'];

/**
 * Hook that runs v2Validate on mount and on tab focus.
 *
 * Responsibilities:
 * - Capture initial division on mount and sync to localStorage
 * - On tab focus: redirect to sign-in if token expired,
 *   auto-reload if user logged in from another tab
 *
 * Division mismatch UI (ring + popup) is handled separately by
 * useDivisionMismatch / AccountDivisionPopover / OmniNavAccountLink.
 */
export function useDivisionCheck(): void {
  const location = useLocation();

  const isCheckingRef = useRef(false);
  const lastCheckTimeRef = useRef(0);
  const hasInitializedRef = useRef(false);
  const wasInitiallyUnauthenticatedRef = useRef(false);
  const CHECK_DEBOUNCE_MS = 5000;
  const currentAppName = getReferrerInfo().appName;

  // V1 sessions don't have an sl-auth-token cookie, so v2Validate would
  // always return {authenticated:false}. The visibility handler below would
  // then redirect a perfectly-valid V1 user back to /sign_in on tab focus.
  // Fold V1 into isSsoPage so both effects skip entirely for V1 sessions,
  // while still calling hooks in a consistent order.
  const isV1Session = getAuthVersion() === 'v1';
  const isOnSsoHost = typeof window !== 'undefined' && isSsoHost(window.location.host);
  const isSsoPage =
    isV1Session || isOnSsoHost || SSO_PATHS.some((path) => location.pathname.startsWith(path));

  const divisionHasAppAccess = useCallback((division: DivisionMembership | undefined): boolean => {
    // `enterprise-sso` is a defensive carve-out kept for safety — in practice
    // SSO hosts now resolve to `advisor` via HOSTNAME_TO_APP, so this branch
    // is only hit by an explicit app-name override.
    if (!division || currentAppName === 'enterprise-sso') {
      return true;
    }
    const accessLevel = division.appAccess?.[currentAppName];
    return Boolean(accessLevel) && accessLevel !== 'blocked';
  }, [currentAppName]);

  const redirectToNoAccess = useCallback((divisionName: string | undefined) => {
    const enterpriseSsoHost = getEnterpriseSsoHost();
    window.location.href = `https://${enterpriseSsoHost}/no-access?app=${encodeURIComponent(currentAppName)}&divName=${encodeURIComponent(divisionName || 'your division')}`;
  }, [currentAppName]);

  useEffect(() => {
    if (isSsoPage) {
      return;
    }

    const captureInitialDivision = async () => {
      try {
        const response = await v2Validate();
        if (response.authenticated && response.division) {
          hasInitializedRef.current = true;
          wasInitiallyUnauthenticatedRef.current = false;
          saveCurrentDivisionId(response.division.id);
          if (response.divisions) {
            saveDivisions(response.divisions);
          }
          if (!divisionHasAppAccess(response.division)) {
            const div = response.division;
            const displayName = div.companyName ? `${div.companyName} ${div.name}` : div.name;
            redirectToNoAccess(displayName);
          }
        } else {
          hasInitializedRef.current = true;
          wasInitiallyUnauthenticatedRef.current = true;
        }
      } catch (error) {
        // Treat a transient initial-validate failure as "unauthenticated for now".
        // Without this, hasInitializedRef stays false and checkDivision early-
        // returns forever, so tab focus does nothing for the rest of the session.
        // Marking unauthenticated lets the next successful focus validate either
        // reload (if the user has since logged in elsewhere) or redirect to
        // sign-in (if they're still not authenticated).
        console.error('[useDivisionCheck] Failed to capture initial division:', error);
        hasInitializedRef.current = true;
        wasInitiallyUnauthenticatedRef.current = true;
      }
    };

    captureInitialDivision();
  }, [isSsoPage]);

  const checkDivision = useCallback(async (): Promise<void> => {
    if (isSsoPage || !hasInitializedRef.current) {
      return;
    }

    const now = Date.now();
    if (now - lastCheckTimeRef.current < CHECK_DEBOUNCE_MS) {
      return;
    }
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;
    lastCheckTimeRef.current = now;

    try {
      const response = await v2Validate();

      if (wasInitiallyUnauthenticatedRef.current && response.authenticated) {
        window.location.reload();
        return;
      }

      if (!response.authenticated) {
        window.location.href = buildSsoSignInUrl(window.location.href);
        return;
      }

      window.dispatchEvent(new CustomEvent<V2ValidateResponse>(DIVISION_VALIDATED_EVENT, { detail: response }));
    } catch (error) {
      console.error('[useDivisionCheck] Failed to validate division:', error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [isSsoPage]);

  useEffect(() => {
    if (isSsoPage) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkDivision();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSsoPage, checkDivision]);
}

export default useDivisionCheck;
